import { alpha, Box, Button, Checkbox, Chip, Dialog, DialogActions, DialogTitle, IconButton, Paper, SortDirection, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, TableSortLabel, Toolbar, Typography, useMediaQuery } from "@mui/material";
import Tooltip from "../../../Components/Atoms/Tooltip";

import qs from "qs";
import { useEffect, useMemo, useState } from "react";
import intl from "react-intl-universal";
import { useHistory } from "react-router";
import {
  DateFilter,
  ScreenGreeting,
} from "../../../Components/Molecules";
import {
  ContentContainer,
  FilterContainer,
  PaperHeader,
} from "./styles";
import { useFilter } from "../../../Hooks/useFilter";
import { TableFilter } from "../../../Components/Molecules/Filters";
import { useAuth } from "../../../Contexts/AuthContext";

import api from "../../../Services/api";
import { ModalLoader } from "../../../Components/Atoms/ModalLoader";
import moment from "moment";
import { formatCurrency, autoFormatDocument } from "../../../Utils/formatters";
import { Download, GetApp } from "@mui/icons-material";

const statusColors = {
  'opened': 'default',
  'Aguardando': 'default',
  'paid': 'success',
  'Pago': 'success',
  'overdue_paid': 'success',
  'canceled': 'error',
  'Cancelado': 'error',
  'overdue': 'warning',
  'Vencido': 'warning',
}

const toCurrency = Intl.NumberFormat('pt-BR', {
  currency: 'BRL',
  style: 'currency'
}).format;

export const Tickets = () => {
  const matches = useMediaQuery("(min-width: 1220px)");

  const [table, setTable] = useState({
    totalRows: 20,
    page: 1,
    page_size: 50,
    items: []
  });

  const [selectedIds, setSelectedIds] = useState(new Set<string>([]));

  const [dashboard, setDashboard] = useState([]);
  const [requestsCount, setRequestsCount] = useState(0);

  const [orderBy, setOrderBy] = useState<Record<string, SortDirection>>({
    CreateAt: 'desc'
  })

  const handleOrderBy = (key: string) => {
    setOrderBy(oldOrderBy => {
      const direction = oldOrderBy[key];

      return {
        [key]: direction === 'asc' ? 'desc' : 'asc'
      }
    })
  }

  const history = useHistory();

  const { hasPermission } = useAuth();

  const { getFilterObj } = useFilter();

  const filters = getFilterObj('tickets');

  const getBoletos = async (params = {}) => {

    try {
      setRequestsCount(prev => {
        prev = prev + 1;
        return prev;
      });

      const response = await api.get('boleto/paged', {
        params: { ...params },
        paramsSerializer: params =>
          qs.stringify(params, { arrayFormat: "repeat" }),
      });

      setTable({
        items: response.data.items,
        page: response.data.currentPage,
        page_size: response.data.pageSize,
        totalRows: response.data.rowCount
      });
    }
    finally {
      setRequestsCount(prev => {
        prev = prev - 1;
        return prev;
      });
    }

  }

  const getBoletoDashboard = async (params = {}) => {
    try {
      setRequestsCount(prev => {
        prev = prev + 1;
        return prev;
      });

      const response = await api.get('boleto/dashboard', {
        params: { ...params }
      });

      setDashboard(response.data);
    }
    finally {
      setRequestsCount(prev => {
        prev = prev - 1;
        return prev;
      });
    }
  }

  const load = async ({
    page = undefined,
    page_size = undefined,
    dashboard = false,
  }) => {

    const orderByQuery = Object.entries(orderBy).map(([key, dir]) => `${key} ${dir}`);

    await getBoletos({
      page: page || table.page,
      page_size: page_size || table.page_size,
      orderBy: orderByQuery,
      ...filters
    });

    if (dashboard) await getBoletoDashboard({ ...filters });
  }

  const batchDownload = async () => {
    try {
      setRequestsCount(prev => {
        prev = prev + 1;
        return prev;
      });

      const response = await api.get('Boleto/print/bulk', {
        params: {
          ...filters,
          ids: Array.from(selectedIds)
        },
        paramsSerializer: params =>
          qs.stringify(params, { arrayFormat: "repeat" }),
        responseType: 'blob'
      });

      const blob = new Blob([response.data], { type: 'application/zip' });
      const downloadUrl = URL.createObjectURL(blob);

      const $a = document.createElement("a");
      $a.href = downloadUrl;
      $a.download = "comprovantes.zip";

      document.body.appendChild($a);
      $a.click();

      $a.remove();
      URL.revokeObjectURL(downloadUrl);
    }
    finally {
      setRequestsCount(prev => {
        prev = prev - 1;
        return prev;
      });
    }
  }

  useEffect(() => {
    load({ page: 1, dashboard: true });
  }, [filters, orderBy]);

  const displayHeader = useMemo(() => {
    if (selectedIds.size === 0) return 'Selecione um boleto para fazer download';
    if (selectedIds.size === 1) return '1 boleto selecionado';

    return `${selectedIds.size} boletos selecionados`;
  }, [selectedIds])

  const isSelectedAll = useMemo(() => {

    for(const row of table.items) {
      if(!selectedIds.has(row.id)) return false;
    }

    return true;

  }, [table.items, selectedIds]);

  return (
    <>
      <ModalLoader open={requestsCount > 0} text={"Carregando informações"} />
      <ScreenGreeting
        title={intl.get(`phrases.myTickets`)}
        subTitle={intl.get(`phrases.emittedTicketDetails`)}
        hasButton={hasPermission('payboleto')}
        buttonTitle={intl.get(`phrases.newTicket`)}
        handleClick={() => history.push('boletos/add')}
      />
      <FilterContainer>
        <TableFilter
          tableName="tickets"
          setPageToFirst={() => { }}
        />
      </FilterContainer>
      {dashboard.length > 0 && (
        <Box
          mb={2}
          sx={{
            overflowX: { xs: "auto", md: "visible" },
            pb: 1,
            "&::-webkit-scrollbar": {
              height: 6,
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "rgba(255,255,255,0.2)",
              borderRadius: 10,
            },
          }}
        >
          <Stack mb={2} direction={"row"} spacing={2}>
            {
              dashboard.map(e => (
              <Paper
                elevation={2}
                key={e.status}
                sx={{
                  p: 3,
                  borderRadius: 3,
                  minWidth: 240,
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                }}
              >
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Typography variant="subtitle2">
                    Status
                  </Typography>
                  <Chip label={e.statusDesc} color={statusColors[e.status]} size="small" />
                </Box>

                <Box>
                  <Typography variant="body2" color={`text.${statusColors[e.status]}`}>
                    Quantidade
                  </Typography>
                  <Typography variant="h5" fontWeight={700}>
                    {e.total.toLocaleString("pt-BR")}
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="body2" color={`text.${statusColors[e.status]}`}>
                    Valor Total
                  </Typography>
                  <Typography variant="h6" fontWeight={700}>
                    { toCurrency(e.amount) }
                  </Typography>
                </Box>
              </Paper>
            ))
            }
          </Stack>
        </Box>
      )}

      <Paper elevation={3}>
        <ContentContainer>
          <PaperHeader
            style={{
              flexDirection: matches ? "row" : "column",
            }}
          >
            <DateFilter
              tableName={"tickets"}
              setPageToFirst={() => load({ page: 1, dashboard: true })}
              hasData={table.items.length > 0}
            />
          </PaperHeader>
          <Toolbar
            sx={{
              pl: { sm: 2 },
              pr: { xs: 1, sm: 1 },
              bgcolor: theme => alpha(theme.palette.primary.main, theme.palette.action.activatedOpacity)
            }}
          >
            <Typography
              sx={{ flex: '1 1 100%' }}
              color="inherit"
              variant="subtitle1"
              component="div"
            >
              {displayHeader}
            </Typography>
            <Tooltip title="Download em massa">
              <Button  disabled={selectedIds.size === 0} onClick={() => batchDownload()}>
                <span>Baixar</span>
                <Download />
              </Button>
            </Tooltip>
          </Toolbar>
          <TableContainer>
            <Table
              size="small"
            >
              <TableHead>
                <TableRow>
                  <TableCell align="center" sx={{ padding: '4px' }}>
                    <Checkbox
                      checked={selectedIds.size === table.totalRows}
                      indeterminate={selectedIds.size > 0 && !isSelectedAll}
                      onChange={() => {
                        if (!isSelectedAll) {
                          const newSelectedIds = table.items.map(e => e.id);
                          setSelectedIds(new Set(newSelectedIds));
                        }
                        else {
                          setSelectedIds(new Set([]));
                        }
                      }}
                    />
                  </TableCell>
                  <TableCell sortDirection={orderBy.PayerName} align="center" sx={{ padding: '4px' }}>
                    <TableSortLabel 
                      direction={orderBy.PayerName || 'asc'} 
                      onClick={() => handleOrderBy('PayerName')}
                      active={orderBy.PayerName !== undefined}>
                        Pagador
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sortDirection={orderBy.CreateAt} align="center" sx={{ padding: '4px' }}>
                    <TableSortLabel 
                      direction={orderBy.CreateAt || 'asc'} 
                      onClick={() => handleOrderBy('CreateAt')}
                      active={orderBy.CreateAt !== undefined}>
                        Emissão
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="center" sx={{ padding: '4px' }}>Vencimento</TableCell>    
                  <TableCell sortDirection={orderBy.Amount} align="center" sx={{ padding: '4px' }}>
                    <TableSortLabel 
                      direction={orderBy.Amount || 'asc'} 
                      onClick={() => handleOrderBy('Amount')}
                      active={orderBy.Amount !== undefined}>
                        Valor
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sortDirection={orderBy.PaymentAt} align="center" sx={{ padding: '4px' }}>
                    <TableSortLabel 
                      direction={orderBy.PaymentAt || 'asc'} 
                      onClick={() => handleOrderBy('PaymentAt')}
                      active={orderBy.PaymentAt !== undefined}>
                        Pagamento
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sortDirection={orderBy.AmountPaid} align="center" sx={{ padding: '4px' }}>
                    <TableSortLabel 
                      direction={orderBy.AmountPaid || 'asc'} 
                      onClick={() => handleOrderBy('AmountPaid')}
                      active={orderBy.AmountPaid !== undefined}>
                        Valor Pago
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="center" sx={{ padding: '4px' }}>Antecipado</TableCell>
                  <TableCell align="center" sx={{ padding: '4px' }}>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {table.items.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell align="center" sx={{ padding: '4px' }}>
                      <Checkbox
                        value={row.id}
                        checked={selectedIds.has(row.id)}
                        onChange={(_, checked) => {
                          setSelectedIds(prev => {
                            const newSelectedIds = new Set(prev);

                            if (checked) newSelectedIds.add(row.id);
                            else newSelectedIds.delete(row.id);

                            return newSelectedIds;
                          });
                        }}
                      />
                    </TableCell>
                    <TableCell align="left" sx={{ padding: '4px' }}>
                      {!!row?.payer?.name ? row?.payer?.name : "-"}<br />
                      {!!row?.payer?.document ? autoFormatDocument(row?.payer?.document) : "-"}
                    </TableCell>
                    <TableCell align="center" sx={{ padding: '4px',fontWeight:"bold" }} title={moment(row?.created_at).format("DD/MM/YYYY HH:mm")}>
                      {moment(row?.created_at).format("DD/MM/YYYY")}
                    </TableCell>
                    <TableCell align="center" sx={{ padding: '4px',fontWeight:"bold" }}>
                      {moment(row?.expiration).format("DD/MM/YYYY")}
                    </TableCell>
                    <TableCell align="right" sx={{ padding: '4px' }}>
                      {formatCurrency(row?.amount)}
                    </TableCell>
                    <TableCell align="center" sx={{ padding: '4px',fontWeight:"bold" }}>
                      {row.payment_at && moment(row?.payment_at).format("DD/MM/YYYY")}
                    </TableCell>
                    <TableCell align="right" sx={{ padding: '4px' }}>
                      {row?.amount_paid==null||row?.amount_paid==0?"":formatCurrency(row?.amount_paid)}
                    </TableCell>
                    <TableCell align="center" sx={{ padding: '4px' }}>
                      <Chip label={row.anticipated ? 'Sim' : 'Não'} color={row.anticipated ? 'success' : 'default'} size="small" />
                    </TableCell>
                    <TableCell align="center" sx={{ padding: '4px' }}>
                      <Chip label={row?.status} color={statusColors[row?.status]} size="small" />
                    </TableCell>
                  </TableRow>
                ))}
                {
                  table.items.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={999} align="center">Não há boletos para serem exibidos</TableCell>
                    </TableRow>
                  )
                }
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component='div'
            onPageChange={(_, page) => load({ page: page + 1 })}
            page={table.page - 1}
            rowsPerPage={table.page_size}
            count={table.totalRows}
            rowsPerPageOptions={[2, 10, 50, 100, 250]}
            onRowsPerPageChange={e => load({ page_size: e.target.value })}
          />
        </ContentContainer>
      </Paper>
    </>
  );
};
